import React, { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { brand } from '../../theme';
import { getSafeRedirect } from '../../utils/safeRedirect';
import {
  CREATOR_ONLY_PAGE_NOTICE,
  EDITOR_HOME_PATH,
  EditorHomeRedirectState,
  isEditorOnlyRole,
} from '../../utils/editorAccount';

/**
 * Signed in for routing purposes. AuthContext only publishes verified users;
 * checking emailVerified here too keeps every guard agreeing on one answer
 * (if they disagreed, /login and /dashboard would redirect to each other).
 */
const isSignedIn = (user: { emailVerified?: boolean } | null | undefined): boolean =>
  Boolean(user) && user!.emailVerified !== false;

const AuthLoadingScreen: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: brand.canvas,
    gap: 2
  }}>
    <CircularProgress
      size={60}
      sx={{
        color: 'primary.main',
        '& .MuiCircularProgress-circle': {
          strokeLinecap: 'round',
        }
      }}
    />
    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

/**
 * Renders its children only for a signed-in, verified user. Nothing
 * protected is rendered while auth is still loading, and a missing user
 * always redirects (replacing the entry, so Back doesn't bounce).
 */
export const PrivateRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthLoadingScreen label="Loading PithPlay..." />;
  }

  if (!isSignedIn(user)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Like PrivateRoute, for pages that only make sense for creators (library,
 * projects, editors, insights, billing, multicam). An editor-only account is
 * sent to its editor dashboard, which says why, instead of a page whose every
 * action the API refuses. Waits for the role, so neither the page nor the
 * redirect flashes before it is known.
 */
export const CreatorRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading, roleLoading } = useAuth();

  if (loading || (isSignedIn(user) && roleLoading)) {
    return <AuthLoadingScreen label="Loading PithPlay..." />;
  }

  if (!isSignedIn(user)) {
    return <Navigate to="/login" replace />;
  }

  if (isEditorOnlyRole(user?.role)) {
    const state: EditorHomeRedirectState = { notice: CREATOR_ONLY_PAGE_NOTICE };
    return <Navigate to={EDITOR_HOME_PATH} replace state={state} />;
  }

  return children;
};

/**
 * The dashboard is the creator home. An editor-only account's home is the
 * editor dashboard (which also shows any pending editor invitations), so
 * sign-in, "/" and every "back to dashboard" link land there for them.
 */
export const DashboardRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading, roleLoading } = useAuth();

  if (loading || (isSignedIn(user) && roleLoading)) {
    return <AuthLoadingScreen label="Loading PithPlay..." />;
  }

  if (!isSignedIn(user)) {
    return <Navigate to="/login" replace />;
  }

  if (isEditorOnlyRole(user?.role)) {
    return <Navigate to={EDITOR_HOME_PATH} replace />;
  }

  return children;
};

export const SuperAdminRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AuthLoadingScreen label="Verifying permissions..." />;
  }

  if (!user || !isSignedIn(user)) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * For /login and /signup: a user who is already signed in (and verified)
 * when they open the page goes to the dashboard, or to a safe `?redirect=`
 * target, instead of seeing a sign-in form.
 *
 * Decided once per navigation, when auth has finished loading. A user who
 * signs in ON the page is not redirected from here: the page navigates
 * itself (the first-run welcome dialog, an invitation's return-to). And
 * unverified users are never redirected - AuthContext reports them as
 * signed out, and they need the sign-in / verification flows.
 */
export const PublicOnlyRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const arrivalRef = useRef<{ key: string; signedIn: boolean } | null>(null);

  if (!loading && arrivalRef.current?.key !== location.key) {
    arrivalRef.current = { key: location.key, signedIn: isSignedIn(user) };
  }

  if (loading) {
    return <AuthLoadingScreen label="Loading PithPlay..." />;
  }

  if (arrivalRef.current?.signedIn && isSignedIn(user)) {
    return <Navigate to={getSafeRedirect(location.search) ?? '/dashboard'} replace />;
  }

  return children;
};
